import { STATE_POPULATION_WEIGHTS } from './state-population-weights';

// Zone to distance mapping in miles
export const zoneToDistanceMap = {
  1: 50, // Zone 1: 0-50 miles
  2: 150, // Zone 2: 51-150 miles
  3: 300, // Zone 3: 151-300 miles
  4: 600, // Zone 4: 301-600 miles
  5: 1000, // Zone 5: 601-1000 miles
  6: 1400, // Zone 6: 1001-1400 miles
  7: 1800, // Zone 7: 1401-1800 miles
  8: 2800, // Zone 8: 1801+ miles (using 2800 as upper bound since that is approximate width of continental US
};

// Zone to fulfillment time mapping in days (includes processing + shipping)
export const zoneToFulfillmentTimeDays = {
  1: 2.0, // Local: 1 day processing + ~1 day shipping
  2: 2.5, // Regional: 1 day processing + ~1.5 days shipping
  3: 3.0, // Extended regional: 1 day processing + ~2 days shipping
  4: 3.5, // Cross-region: 1 day processing + ~2.5 days shipping
  5: 4.5, // Far: 1 day processing + ~3.5 days shipping
  6: 5.0, // Farther: 1 day processing + ~4 days shipping
  7: 6.0, // Cross-country: 1 day processing + ~5 days shipping
  8: 7.0, // Maximum distance: 1 day processing + ~6 days shipping
};

/**
 * Generic interpolation helper that maps a fractional zone value to an interpolated metric value
 * @param {number} avgZone - The average zone value (can be fractional)
 * @param {Record<number, number>} zoneToValueMap - Map of zone number to metric value
 * @returns {number | null} Interpolated value, or null if zone is not found in map
 */
const interpolateZoneValue = (avgZone, zoneToValueMap) => {
  const lowerZone = Math.floor(avgZone);
  const upperZone = Math.ceil(avgZone);

  const lowerValue = zoneToValueMap[lowerZone];
  if (lowerValue === undefined) {
    return null;
  }

  if (lowerZone === upperZone) {
    return lowerValue;
  }

  const upperValue = zoneToValueMap[upperZone];
  if (upperValue === undefined) {
    return lowerValue;
  }

  const fraction = avgZone - lowerZone;
  return lowerValue + (upperValue - lowerValue) * fraction;
};

/**
 * Calculates weighted average zone across states using population-based weights.
 * @param {Map<string, number>} stateZoneMap - Map of state code to zone number
 * @returns {number} The weighted average zone
 */
export const calculateAverageZone = (stateZoneMap) => {
  const states = Array.from(stateZoneMap.keys());

  if (states.length === 0) {
    return 0;
  }

  let weightedSum = 0;
  let totalWeight = 0;

  states.forEach((stateCode) => {
    const zone = stateZoneMap.get(stateCode);
    const weight = STATE_POPULATION_WEIGHTS.get(stateCode) ?? 0;

    weightedSum += zone * weight;
    totalWeight += weight;
  });

  // Normalize by total weight to handle cases where weights don't sum to 1
  return totalWeight > 0 ? weightedSum / totalWeight : 0;
};

/**
 * Calculates the average distance in miles based on average zone, interpolating between zone boundaries
 * @param {number} avgZone - The average zone value
 * @returns {number | null} Average distance in miles, or null if zone is not found in map
 */
export const calculateAverageDistance = (avgZone) => {
  return interpolateZoneValue(avgZone, zoneToDistanceMap);
};

/**
 * Calculates estimated fulfillment time including processing and shipping based on average zone, interpolating between zones
 * @param {number} avgZone - The average zone value
 * @returns {number | null} Average fulfillment time in days, or null if zone is not found in map
 */
export const calculateFulfillmentTime = (avgZone) => {
  return interpolateZoneValue(avgZone, zoneToFulfillmentTimeDays);
};
